/**
 * openaiService.ts - OpenAI API Integration Service
 *
 * This service provides integration with OpenAI's API for AI-powered features
 * including image description generation, image editing, and image generation.
 * Handles API key management, model selection, and error handling.
 *
 * Key Features:
 * - Settings management with localStorage persistence
 * - Image description generation from uploaded images
 * - Image editing with natural language prompts
 * - Image generation from text descriptions
 * - Model availability checking and filtering
 * - Comprehensive error handling and validation
 *
 * Supported Models:
 * - Text: gpt-5, gpt-4o, gpt-4-turbo, gpt-4
 * - Image: gpt-image-1
 *
 * Dependencies: OpenAI API, localStorage, Canvas API
 */

import { OpenAISettingsDataModel } from "@mindmemobile/mm-payment-modules";

// OpenAI API integration service
// interface OpenAISettings {
//   apiKey: string;
//   textModel: string;
//   imageModel: string;
// }

interface OpenAIResponse {
  success: boolean;
  data?: any;
  error?: string;
}

export class OpenAIService {
  private settings: OpenAISettingsDataModel | null = null;

  constructor(settings: OpenAISettingsDataModel) {
    this.settings = settings;
  }  // Initialize and update API key on service load
  // static init() {
  //   this.updateApiKey();
  // }

  // Set default values
  // static getDefaultSettings(): OpenAISettings {
  //   return {
  //     apiKey:
  //       "<removed: key comes from ApplicationSettings>",
  //     textModel: "gpt-4o",
  //     imageModel: "gpt-image-1",
  //   };
  // }

  // static loadSettings(): OpenAISettings | null {
  //   try {
  //     const saved = localStorage.getItem("openai_settings");
  //     if (saved) {
  //       const parsedSettings = JSON.parse(saved);
  //       // Force correct model and API key regardless of what's saved
  //       parsedSettings.textModel = "gpt-4o";
  //       parsedSettings.apiKey =
  //         "<removed: key comes from ApplicationSettings>";
  //       this.settings = parsedSettings;
  //       // Save the updated settings back to localStorage
  //       this.saveSettings(parsedSettings);
  //       return this.settings;
  //     }
  //     // Return default settings if none saved
  //     this.settings = this.getDefaultSettings();
  //     return this.settings;
  //   } catch (error) {
  //     console.error("Error loading OpenAI settings:", error);
  //   }
  //   // Return default settings on error
  //   this.settings = this.getDefaultSettings();
  //   return this.settings;
  // }

  saveSettings(settings: OpenAISettingsDataModel): boolean {
    try {
      //localStorage.setItem("openai_settings", JSON.stringify(settings));
      this.settings = settings;
      return true;
    } catch (error) {
      console.error("Error saving OpenAI settings:", error);
      return false;
    }
  }

  // static getSettings(): OpenAISettings | null {
  //   if (!this.settings) {
  //     return this.loadSettings();
  //   }
  //   return this.settings;
  // }

  hasValidApiKey(): boolean {
    const settings = this.settings;
    const hasKey = !!(
      settings?.apiKey &&
      settings.apiKey.trim() !== "" &&
      settings.apiKey !== "your-api-key-here"
    );
    return hasKey;
  }

  async testConnection(): Promise<OpenAIResponse> {
    if (!this.settings?.apiKey) {
      return { success: false, error: "OpenAI API key not configured" };
    }

    try {
      console.log("Testing OpenAI connection...");

      const response = await fetch(
        "https://api.openai.com/v1/chat/completions",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${this.settings.apiKey}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            model: "gpt-4o",
            messages: [
              {
                role: "user",
                content: "Respond with just: Hello, I am working!",
              },
            ],
            max_tokens: 20,
          }),
        }
      );

      console.log("Test Response Status:", response.status);

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Test API Error:", errorData);
        throw new Error(
          errorData.error?.message || `OpenAI API error: ${response.status}`
        );
      }

      const data = await response.json();
      console.log("Test Response Data:", data);

      const content = data.choices?.[0]?.message?.content?.trim();
      console.log("Test Content:", content);

      return { success: true, data: content };
    } catch (error) {
      console.error("Test Connection Error:", error);
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Failed to test connection",
      };
    }
  }

  async fetchAvailableModels(): Promise<{
    textModels: string[];
    imageModels: string[];
  }> {
    const settings = this.settings;
    if (!settings?.apiKey) {
      console.error("No API key available for fetching models");
      return {
        textModels: ["gpt-4o", "gpt-4-turbo", "gpt-4"],
        imageModels: ["gpt-image-1"],
      };
    }

    try {
      console.log("🔍 Fetching available models from OpenAI API...");

      const response = await fetch("https://api.openai.com/v1/models", {
        headers: {
          Authorization: `Bearer ${settings.apiKey}`,
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("❌ Failed to fetch models:", response.status, errorText);
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();

      // Filter for GPT models
      const allModels = data.data || [];
      const gptModels = allModels
        .filter((model: any) => model.id.includes("gpt"))
        .map((model: any) => model.id)
        .sort();

      // Filter for GPT-5 models specifically
      const gpt5Models = gptModels.filter((id: string) => id.includes("gpt-5"));
      console.log("🚀 GPT-5 models found:", gpt5Models);

      // Filter for text vs image models
      const textModels = gptModels.filter(
        (id: string) =>
          !id.includes("image") &&
          !id.includes("vision") &&
          !id.includes("dall-e")
      );

      const imageModels = gptModels.filter(
        (id: string) =>
          id.includes("image") || id.includes("vision") || id.includes("dall-e")
      );

      console.log("📝 Text models:", textModels);
      console.log("🖼️ Image models:", imageModels);

      return {
        textModels:
          textModels.length > 0
            ? textModels
            : ["gpt-4o", "gpt-4-turbo", "gpt-4"],
        imageModels: imageModels.length > 0 ? imageModels : ["gpt-image-1"],
      };
    } catch (error) {
      console.error("💥 Error fetching models:", error);
      // Return fallback models
      return {
        textModels: ["gpt-4o", "gpt-4-turbo", "gpt-4"],
        imageModels: ["gpt-image-1"],
      };
    }
  }

  async generateImageDescription(
    imageUrl: string,
    fundraiserTitle?: string,
    fundraiserDescription?: string
  ): Promise<OpenAIResponse> {
    const settings = this.settings
    if (!settings?.apiKey) {
      return { success: false, error: "OpenAI API key not configured" };
    }

    try {
      const contextInfo: string[] = [];
      if (fundraiserTitle) contextInfo.push(`Fundraiser: ${fundraiserTitle}`);
      if (fundraiserDescription)
        contextInfo.push(`Context: ${fundraiserDescription}`);

      const contextString =
        contextInfo.length > 0 ? `\n\nContext: ${contextInfo.join(". ")}` : "";

      const prompt = `Generate a short description of this specific image in 20 words or less.${contextString}`;

      const response = await fetch(
        "https://api.openai.com/v1/chat/completions",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${settings.apiKey}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            model: settings.textModel || "gpt-4o",
            messages: [
              {
                role: "user",
                content: [
                  {
                    type: "text",
                    text: prompt,
                  },
                  {
                    type: "image_url",
                    image_url: {
                      url: imageUrl,
                    },
                  },
                ],
              },
            ],
            max_tokens: 50,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error?.message || `OpenAI API error: ${response.status}`
        );
      }

      const data = await response.json();
      const description = data.choices?.[0]?.message?.content?.trim();

      if (!description) {
        throw new Error("No description generated");
      }

      return { success: true, data: description };
    } catch (error) {
      console.error("Error generating image description:", error);
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to generate description",
      };
    }
  }

  async editImage(
    imageUrl: string,
    prompt: string
  ): Promise<OpenAIResponse> {
    const settings = this.settings;
    if (!settings?.apiKey) {
      return { success: false, error: "OpenAI API key not configured" };
    }

    try {
      // Fetch the image to get a Blob
      const imageResponse = await fetch(imageUrl);
      if (!imageResponse.ok) {
        throw new Error("Failed to fetch image");
      }
      const imageBlob = await imageResponse.blob();

      // Convert to PNG with alpha channel using canvas to ensure RGBA format
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();

      // Load image into canvas
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = URL.createObjectURL(imageBlob);
      });

      canvas.width = img.width;
      canvas.height = img.height;
      ctx?.drawImage(img, 0, 0);

      // Convert canvas to PNG blob (RGBA format)
      const pngBlob = await new Promise<Blob>((resolve) => {
        canvas.toBlob((blob) => {
          resolve(blob!);
        }, "image/png");
      });

      // Clean up object URL
      URL.revokeObjectURL(img.src);

      // Convert PNG Blob to File
      const imageFile = new File([pngBlob], "image.png", { type: "image/png" });

      // Ensure we use a supported model for image editing
      // Image editing only supports: 'dall-e-2', 'gpt-image-1', 'gpt-image-0721-mini-alpha'
      let editingModel = "dall-e-2"; // Default fallback
      if (
        settings.imageModel === "gpt-image-1" ||
        settings.imageModel === "gpt-image-0721-mini-alpha"
      ) {
        editingModel = settings.imageModel;
      }

      // Create FormData for multipart/form-data request
      const formData = new FormData();
      formData.append("image", imageFile);
      formData.append("prompt", prompt);
      formData.append("model", editingModel);
      formData.append("n", "1");
      formData.append("size", "1024x1024");

      const response = await fetch("https://api.openai.com/v1/images/edits", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${settings.apiKey}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error?.message || `OpenAI API error: ${response.status}`
        );
      }

      const data = await response.json();
      const editedImageUrl = data.data?.[0]?.url;

      if (!editedImageUrl) {
        throw new Error("No edited image returned");
      }

      return { success: true, data: editedImageUrl };
    } catch (error) {
      console.error("Error editing image:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to edit image",
      };
    }
  }

  async generateImage(prompt: string): Promise<OpenAIResponse> {
    const settings = this.settings
    if (!settings?.apiKey) {
      return { success: false, error: "OpenAI API key not configured" };
    }

    try {
      const response = await fetch(
        "https://api.openai.com/v1/images/generations",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${settings.apiKey}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            model: settings.imageModel || "gpt-image-1",
            prompt: prompt,
            n: 1,
            size: "1024x1024",
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error?.message || `OpenAI API error: ${response.status}`
        );
      }

      const data = await response.json();

      // Check different possible response formats for gpt-image-1
      const generatedImageUrl =
        data.data?.[0]?.url || data.url || data.image_url;

      if (!generatedImageUrl) {
        console.error("❌ No image URL found in response structure:", {
          hasData: !!data.data,
          dataLength: data.data?.length,
          firstItem: data.data?.[0],
          responseKeys: Object.keys(data),
          fullResponse: data,
        });
        throw new Error(
          "No image generated - API returned response without image URL"
        );
      }

      console.log("✅ Image generation successful!");
      return { success: true, data: generatedImageUrl };
    } catch (error) {
      console.error("Error generating image:", error);
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Failed to generate image",
      };
    }
  }

  async analyzeDescriptionAndGetQuestions(
    description: string,
    conversationHistory: string[] = [],
    completedFields: string[] = []
  ): Promise<OpenAIResponse> {
    const settings = this.settings
    if (!settings?.apiKey) {
      return { success: false, error: "OpenAI API key not configured" };
    }

    console.log("Analyzing description for missing information...", {
      description,
      conversationHistory,
      completedFields,
      model: settings.textModel,
      apiKeyExists: !!settings.apiKey,
    });

    try {
      const conversationContext =
        conversationHistory.length > 0
          ? `\n\nConversation history:\n${conversationHistory.join("\n")}`
          : "";

      const completedFieldsContext =
        completedFields.length > 0
          ? `\n\nFields already completed: ${completedFields.join(", ")}`
          : "";

      const prompt = `Analyze this fundraiser description and determine if we have enough information to create a complete fundraiser:

"${description}"${conversationContext}${completedFieldsContext}

CAREFULLY ANALYZE the description for these key pieces of information:
1. GOAL AMOUNT: Look for dollar amounts, fundraising targets, or financial goals
2. BENEFICIARIES: Who will benefit from this fundraiser? (team, organization, individuals, etc.)
3. FUND USAGE: How will the money be used? (equipment, travel, medical bills, etc.)
4. TIMELINE: Look for start dates, end dates, deadlines (e.g., "Start Oct 1", "end Feb 28", "by December")
5. PURPOSE/MISSION: What is the cause or reason for fundraising?
6. PROMOTER STRATEGY: Do they want individual promoters/fundraisers? If they say "no promoters" or similar, DO NOT ask about promoter goals or incentives

DO NOT ASK ABOUT:
- General marketing strategy (not needed for fundraiser creation)
- Social media plans (not required)
- Advertising methods (not essential)
- Promotional tactics (handled separately)

CRITICAL: If dates are mentioned (like "Start Oct 1 and end Feb 28, 2026"), DO NOT ask "When do you need funds by?" - the timeline is already provided.

CONTEXT AWARENESS: 
- If timeline/dates are mentioned, don't ask about timeline
- If the user indicates they don't want promoters, do not ask any promoter-related questions
- If goal amount is stated, don't ask about fundraising goal

IMPORTANT: Only ask questions for information that is truly missing or unclear from the description. Use context and common sense.

NEVER ASK THESE QUESTIONS:
- "Do you have a specific marketing strategy for this fundraiser?" (not needed)
- "How do you plan to promote this fundraiser?" (not required for setup)
- "What social media platforms will you use?" (not essential)
- "Do you have any promotional materials?" (not needed for creation)

Examples of COMPLETE information:
- "HVAA Boys Soccer. Start Oct 1 and end Feb 28, 2026. Overall goal is $25,000. Benefits the team with funds used for uniforms and travel."
  → This has goal ($25,000), beneficiaries (HVAA Boys Soccer team), usage (uniforms and travel), timeline (Oct 1 - Feb 28, 2026)
  → DO NOT ask for goal, beneficiaries, fund usage, or timeline - all key information is provided
  → COMPLETE - no additional questions needed unless promoter strategy is unclear

- "Help my medical bills after surgery. Need $5,000 by December."
  → Has goal ($5,000), timeline (by December), purpose (medical bills)
  → DO NOT ask about goal or timeline since both are provided
  → Only ask about missing details if truly needed

- "Community garden project. $10,000 goal. No individual fundraisers."
  → Has goal, indicates no promoters
  → DO NOT ask about promoter strategy or timeline if dates are mentioned
  → ASK: Only about truly missing information

- "Youth soccer team fundraiser for uniforms and travel. Goal is $15,000."
  → Has beneficiaries (youth soccer team), usage (uniforms and travel), goal ($15,000)
  → DO NOT ask about marketing strategy - this is handled in a separate flow
  → COMPLETE - move to promoter questions in the marketing flow

Return ONLY this JSON structure:

{
  "hasEnoughInfo": true/false,
  "questions": [],
  "missingFields": [],
  "nextQuestion": ""
}

If hasEnoughInfo is true, return empty questions array.
If hasEnoughInfo is false, include ONLY questions for truly missing information.
Maximum 2 questions per response.`;

      console.log("🚀 Making OpenAI API request for analysis...");

      // Test with a simple request first to isolate the issue
      console.log("🧪 Testing simple API call first...");
      try {
        const testResponse = await fetch(
          "https://api.openai.com/v1/chat/completions",
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${settings.apiKey}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              model: "gpt-4o",
              messages: [{ role: "user", content: 'Say "test"' }],
              max_tokens: 5,
            }),
          }
        );

        console.log("🧪 Test response status:", testResponse.status);
        if (!testResponse.ok) {
          const testError = await testResponse.text();
          console.error("🧪 Test failed:", testError);
        } else {
          console.log("🧪 Test passed - API is working");
        }
      } catch (testError) {
        console.error("🧪 Test request failed:", testError);
      }

      // Try with a more reliable model first
      const modelToUse =
        settings.textModel === "gpt-4o" ? "gpt-4o" : settings.textModel;

      const requestBody = {
        model: modelToUse,
        messages: [
          {
            role: "system",
            content:
              "You are an expert at analyzing fundraiser descriptions to identify missing key information. Always return valid JSON only.",
          },
          {
            role: "user",
            content: prompt,
          },
        ],
        max_tokens: 400,
        temperature: 0.7,
      };

      console.log("📤 Request body:", JSON.stringify(requestBody, null, 2));
      console.log("⏱️ Making analysis API call at:", new Date().toISOString());

      const response = await fetch(
        "https://api.openai.com/v1/chat/completions",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${settings.apiKey}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );

      console.log(
        "⏱️ Analysis API response received at:",
        new Date().toISOString()
      );
      console.log("📡 OpenAI response status:", response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error("❌ OpenAI Analysis Error:", {
          status: response.status,
          statusText: response.statusText,
          errorText: errorText,
        });

        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { error: { message: errorText } };
        }

        throw new Error(
          errorData.error?.message ||
          `OpenAI API error: ${response.status} - ${errorText}`
        );
      }

      const data = await response.json();
      console.log("📊 Full OpenAI Response:", JSON.stringify(data, null, 2));

      const content = data.choices?.[0]?.message?.content?.trim();
      console.log("📝 Extracted Content:", content);
      console.log("📋 Response Structure Check:", {
        hasChoices: !!data.choices,
        choicesLength: data.choices?.length,
        firstChoice: data.choices?.[0],
        hasMessage: !!data.choices?.[0]?.message,
        hasContent: !!data.choices?.[0]?.message?.content,
        contentLength: data.choices?.[0]?.message?.content?.length,
      });

      if (!content) {
        console.error("❌ No content found in OpenAI response");
        console.log("🔄 Attempting retry with gpt-4o model...");

        // Retry with gpt-4o if the original model failed
        try {
          const retryResponse = await fetch(
            "https://api.openai.com/v1/chat/completions",
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${settings.apiKey}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                model: "gpt-4o",
                messages: [
                  {
                    role: "system",
                    content:
                      "You are an expert at analyzing fundraiser descriptions. Return valid JSON with hasEnoughInfo (boolean), questions (array), missingFields (array), and nextQuestion (string).",
                  },
                  {
                    role: "user",
                    content: `Analyze this fundraiser description and determine if we have enough information: "${description}". Return JSON format only.`,
                  },
                ],
                max_tokens: 300,
                temperature: 0.5,
              }),
            }
          );

          if (retryResponse.ok) {
            const retryData = await retryResponse.json();
            const retryContent =
              retryData.choices?.[0]?.message?.content?.trim();
            console.log("🔄 Retry response:", retryContent);

            if (retryContent) {
              console.log("✅ Retry successful with gpt-4o");
              return { success: true, data: JSON.parse(retryContent) };
            }
          }
        } catch (retryError) {
          console.error("🔄 Retry failed:", retryError);
        }

        throw new Error(
          "No analysis generated - OpenAI returned empty content"
        );
      }

      console.log("📝 Raw Analysis Content:", content);

      const sanitizeContent = (input: string): string => {
        let sanitized = input.trim();

        const codeFenceMatch = sanitized.match(/^```[\w-]*\s*([\s\S]*?)```$/);
        if (codeFenceMatch) {
          sanitized = codeFenceMatch[1].trim();
        }

        const extractFirstJsonObject = (value: string): string | null => {
          const startIndex = value.indexOf("{");
          if (startIndex === -1) {
            return null;
          }

          let depth = 0;
          let inString = false;
          let isEscaped = false;

          for (let i = startIndex; i < value.length; i++) {
            const char = value[i];

            if (inString) {
              if (isEscaped) {
                isEscaped = false;
              } else if (char === "\\") {
                isEscaped = true;
              } else if (char === '"') {
                inString = false;
              }
            } else {
              if (char === '"') {
                inString = true;
              } else if (char === "{") {
                depth++;
              } else if (char === "}") {
                depth--;
                if (depth === 0) {
                  return value.slice(startIndex, i + 1).trim();
                }
              }
            }
          }

          return null;
        };

        const firstObject = extractFirstJsonObject(sanitized);
        if (firstObject) {
          return firstObject;
        }

        return sanitized;
      };

      const sanitizedContent = sanitizeContent(content);
      console.log("🧼 Sanitized Analysis Content:", sanitizedContent);

      let analysisData;
      try {
        analysisData = JSON.parse(sanitizedContent);
        console.log("✅ Parsed Analysis Data:", analysisData);
      } catch (parseError) {
        console.error(
          "❌ Error parsing analysis response:",
          parseError,
          "Sanitized Content:",
          sanitizedContent
        );
        throw new Error("Invalid analysis response format");
      }

      return { success: true, data: analysisData };
    } catch (error) {
      console.error("Error analyzing description:", error);
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to analyze description",
      };
    }
  }

  static async askFinalImageQuestion(): Promise<OpenAIResponse> {
    // This is a simple method that returns the final question
    // The actual logic will be handled by the calling component
    return {
      success: true,
      data: {
        question: "Do you want us to select an image for your fundraiser?",
        isComplete: true,
        requiresImageGeneration: true,
      },
    };
  }

  async generateNextQuestion(
    conversationHistory: string[],
    missingFields: string[]
  ): Promise<OpenAIResponse> {
    const settings = this.settings
    if (!settings?.apiKey) {
      return { success: false, error: "OpenAI API key not configured" };
    }

    console.log("Generating next question...", {
      conversationHistory,
      missingFields,
    });

    try {
      const prompt = `You are an expert at writing engaging fundraiser content to capture donations. Based on the conversation history and missing fields, generate the next most important question to ask the user.

Conversation history:
${conversationHistory.join("\n")}

Missing fields: ${missingFields.join(", ")}

Generate a single, targeted question to capture the most critical missing information. Focus on:
1. Fundraising goal amount (if missing)
2. How funds will be used
3. Who will benefit
4. Timeline/urgency
5. Specific impact metrics

Return ONLY this JSON structure:

{
  "question": "What is your target fundraising goal amount?",
  "priority": "high",
  "fieldTargets": ["goal", "fundUsage"]
}`;

      const response = await fetch(
        "https://api.openai.com/v1/chat/completions",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${settings.apiKey}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            model: "gpt-4o",
            messages: [
              {
                role: "system",
                content:
                  "You are an expert at generating targeted questions to gather fundraiser information. Always return valid JSON only.",
              },
              {
                role: "user",
                content: prompt,
              },
            ],
            max_tokens: 200,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        console.error("OpenAI Question Generation Error:", errorData);
        throw new Error(
          errorData.error?.message || `OpenAI API error: ${response.status}`
        );
      }

      const data = await response.json();
      const content = data.choices?.[0]?.message?.content?.trim();

      if (!content) {
        throw new Error("No question generated");
      }

      let questionData;
      try {
        questionData = JSON.parse(content);
      } catch (parseError) {
        console.error(
          "Error parsing question response:",
          parseError,
          "Content:",
          content
        );
        throw new Error("Invalid question response format");
      }

      return { success: true, data: questionData };
    } catch (error) {
      console.error("Error generating next question:", error);
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to generate question",
      };
    }
  }

  async generateImageSearchTerms(
    description: string,
    title: string,
    category: string
  ): Promise<OpenAIResponse> {
    const settings = this.settings;
    if (!settings?.apiKey) {
      return { success: false, error: "OpenAI API key not configured" };
    }

    try {
      const prompt = `Generate 3-5 simple, effective search terms for finding a professional fundraiser image on Unsplash.

Fundraiser: "${title}"
Description: "${description}"
Category: ${category}

Create search terms that would find appropriate, professional images for this fundraiser. Focus on:
- The main subject/cause (e.g., "soccer team", "medical care", "education")
- The setting/context (e.g., "sports field", "hospital", "classroom")
- The emotion/tone (e.g., "teamwork", "hope", "community")

Return ONLY a simple comma-separated list of 3-5 search terms, no other text.

Example: "soccer team, sports field, teamwork, youth athletes, community support"`;

      const response = await fetch(
        "https://api.openai.com/v1/chat/completions",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${settings.apiKey}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            model: "gpt-4o",
            messages: [
              {
                role: "system",
                content:
                  "You are an expert at generating effective image search terms. Return only the search terms, nothing else.",
              },
              {
                role: "user",
                content: prompt,
              },
            ],
            max_tokens: 50,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error?.message || `OpenAI API error: ${response.status}`
        );
      }

      const data = await response.json();
      const searchTerms = data.choices?.[0]?.message?.content?.trim();

      if (!searchTerms) {
        throw new Error("No search terms generated");
      }

      return { success: true, data: searchTerms };
    } catch (error) {
      console.error("Error generating search terms:", error);
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to generate search terms",
      };
    }
  }

  async listGPT5Models(): Promise<OpenAIResponse> {
    console.log("🔍 Fetching and testing GPT-5 models...");

    try {
      const models = await this.fetchAvailableModels();
      const allModels = [...models.textModels, ...models.imageModels];

      // Filter for GPT-5 models
      const gpt5Models = allModels.filter((model) =>
        model.toLowerCase().includes("gpt-5")
      );

      console.log("🚀 Found GPT-5 models:", gpt5Models);

      // Test each GPT-5 model
      const testResults: {
        model: string;
        status: string;
        working: boolean;
        error: string;
      }[] = [];

      for (const model of gpt5Models) {
        console.log(`🧪 Testing model: ${model}`);

        try {
          const testResponse = await fetch(
            "https://api.openai.com/v1/chat/completions",
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${this.settings?.apiKey}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                model: model,
                messages: [{ role: "user", content: 'Say "working"' }],
                max_tokens: 5,
              }),
            }
          );

          const result = {
            model: model,
            status: testResponse.status?.toString(),
            working: testResponse.ok,
            error: "",
          };

          if (!testResponse.ok) {
            const errorText = await testResponse.text();
            result.error = errorText;
          }

          testResults.push(result);
          console.log(
            `${testResponse.ok ? "✅" : "❌"} ${model}: ${testResponse.status}`
          );
        } catch (error) {
          testResults.push({
            model: model,
            status: "error",
            working: false,
            error: error instanceof Error ? error.message : "Unknown error",
          });
          console.log(`❌ ${model}: ${error}`);
        }
      }

      return {
        success: true,
        data: {
          gpt5Models: gpt5Models,
          testResults: testResults,
          workingModels: testResults
            .filter((r) => r.working)
            .map((r) => r.model),
        },
      };
    } catch (error) {
      console.error("💥 Error listing GPT-5 models:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to list models",
      };
    }
  }

  async generateFundraiserImage(
    description: string,
    fundraiserTitle: string
  ): Promise<OpenAIResponse> {
    const settings = this.settings;
    if (!settings?.apiKey) {
      return { success: false, error: "OpenAI API key not configured" };
    }

    console.log("Generating fundraiser image with OpenAI...", {
      description,
      title: fundraiserTitle,
    });

    try {
      const prompt = `Create a professional, inspiring image for a fundraiser titled "${fundraiserTitle}". 
      
      Description: ${description}
      
      Style: Professional, uplifting, and appropriate for fundraising. Should evoke emotion and inspire donations. High quality, well-composed, and visually appealing.`;

      // Use the configured model directly
      const modelToUse = settings.imageModel || "gpt-image-1";

      console.log("Using image model:", modelToUse);

      const response = await fetch(
        "https://api.openai.com/v1/images/generations",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${settings.apiKey}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            model: modelToUse,
            prompt: prompt,
            n: 1,
            size: "1024x1024",
          }),
        }
      );

      console.log("Image Generation Response Status:", response.status);

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Image Generation Error:", errorData);
        throw new Error(
          errorData.error?.message ||
          `OpenAI Image API error: ${response.status}`
        );
      }

      const data = await response.json();
      console.log(
        "📊 Full Fundraiser Image Response:",
        JSON.stringify(data, null, 2)
      );

      // Check different possible response formats for gpt-image-1
      const imageUrl = data.data?.[0]?.url || data.url || data.image_url;

      if (!imageUrl) {
        console.error("❌ No image URL found in fundraiser response:", {
          hasData: !!data.data,
          dataLength: data.data?.length,
          firstItem: data.data?.[0],
          responseKeys: Object.keys(data),
          fullResponse: data,
        });
        throw new Error(
          "No image generated - API returned response without image URL"
        );
      }

      console.log("✅ Image generation successful!");
      return { success: true, data: imageUrl };
    } catch (error) {
      console.error("Error generating fundraiser image:", error);
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Failed to generate image",
      };
    }
  }

  async parseRewardsByAmount(
    userInput: string
  ): Promise<OpenAIResponse> {
    const settings = this.settings;
    if (!settings?.apiKey) {
      return { success: false, error: "OpenAI API key not configured" };
    }

    try {
      const prompt = `Parse this user input about promoter rewards by donation amount and extract structured reward data:

User Input: "${userInput}"

Look for patterns like:
- "Shirt @ $100" = Shirt reward at $100 level
- "Lunch @ $500" = Lunch reward at $500 level  
- "Dinner @ 2,000" = Dinner reward at $2,000 level
- "tee shirt if you raise $500" = Tee Shirt reward at $500 level
- "gift card at $2,500" = Gift Card reward at $2,500 level
- "Trip @ 10,000 raised" = Trip reward at $10,000 level
- "Shirt at $1,000 raised" = Shirt reward at $1,000 level
- "Trip at 10,000" = Trip reward at $10,000 level
- "Shirt at 1,000" = Shirt reward at $1,000 level

Extract ALL reward and amount pairs you can find. Be very flexible with formatting:
- Amounts might have $ signs, commas, or be plain numbers
- Words like "raised", "donated", "at", "@", "for" can separate rewards and amounts
- Numbers like 10,000 should become 10000
- Numbers like 1,000 should become 1000

Return ONLY this JSON structure (no other text):

{
  "rewards": [
    {
      "level": 100,
      "reward": "Shirt",
      "order": 1
    },
    {
      "level": 500,
      "reward": "Lunch", 
      "order": 2
    }
  ]
}

CRITICAL: Always return valid JSON only. Extract dollar amounts as pure numbers (remove $, commas). Order by amount (lowest first). If you find ANY reward/amount pairs, include them. Only return empty array if absolutely no rewards can be identified.`;

      const response = await fetch(
        "https://api.openai.com/v1/chat/completions",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${settings.apiKey}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            model: "gpt-4o",
            messages: [
              {
                role: "system",
                content:
                  "You are an expert at parsing reward information. Always return valid JSON only.",
              },
              {
                role: "user",
                content: prompt,
              },
            ],
            max_tokens: 300,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error?.message || `OpenAI API error: ${response.status}`
        );
      }

      const data = await response.json();
      const content = data.choices?.[0]?.message?.content?.trim();

      if (!content) {
        throw new Error("No rewards parsing generated");
      }

      console.log("🔍 Raw OpenAI response for rewards parsing:", content);

      let rewardsData;
      try {
        // Clean the content in case there's extra text
        let cleanContent = content.trim();

        // Extract JSON if it's wrapped in other text
        const jsonMatch = cleanContent.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          cleanContent = jsonMatch[0];
        }

        rewardsData = JSON.parse(cleanContent);
        console.log("✅ Parsed rewards data:", rewardsData);
      } catch (parseError) {
        console.error(
          "❌ Error parsing rewards response:",
          parseError,
          "Content:",
          content
        );

        // Try to extract rewards manually as fallback
        const fallbackRewards = this.extractRewardsFallback(userInput);
        if (fallbackRewards.length > 0) {
          console.log("🔄 Using fallback parsing:", fallbackRewards);
          return { success: true, data: fallbackRewards };
        }

        throw new Error("Invalid rewards response format");
      }

      const rewards = rewardsData.rewards || [];
      console.log("🎁 Final rewards array:", rewards);

      return { success: true, data: rewards };
    } catch (error) {
      console.error("Error parsing rewards by amount:", error);
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Failed to parse rewards",
      };
    }
  }

  async parseRewardsByRank(userInput: string): Promise<OpenAIResponse> {
    const settings = this.settings
    if (!settings?.apiKey) {
      return { success: false, error: "OpenAI API key not configured" };
    }

    try {
      const prompt = `Parse this user input about promoter rewards by rank/position and extract structured reward data:

User Input: "${userInput}"

Look for patterns like:
- "Trip for top position" = Trip reward for #1 rank
- "must raise $2,500" = minimum requirement of $2,500
- "#1 gets trip" = Trip reward for #1 rank
- "top 3 get shirts" = Shirt reward for #1-3 ranks
- "winner gets $100" = $100 reward for #1 rank
- "$500 gift card if #1 position and raise a minimum of 10,000" = $500 Gift Card for #1 rank, minimum $10,000
- "$250 gift card if 2-3 position and raise a min of 5,000" = $250 Gift Card for #2-3 ranks, minimum $5,000
- "$100 gift card if 4-10 position and raise a minimum donation of $2,500" = $100 Gift Card for #4-10 ranks, minimum $2,500

Extract rank-based rewards. Be very flexible with formatting:
- Ranks can be "#1", "1st", "top position", "winner", "2-3", "4-10", etc.
- Rewards can be dollar amounts like "$500 gift card" or items like "Trip"
- Minimums can be "raise a minimum of 10,000", "must raise $2,500", "min of 5,000", etc.
- Convert all dollar amounts to pure numbers (remove $, commas)
- Convert rank ranges like "2-3" to "#2-3", "4-10" to "#4-10"

Return ONLY this JSON structure (no other text):

{
  "rewards": [
    {
      "rank": "#1",
      "reward": "$500 Gift Card",
      "minimum": 10000,
      "order": 1
    },
    {
      "rank": "#2-3",
      "reward": "$250 Gift Card",
      "minimum": 5000,
      "order": 2
    }
  ]
}

CRITICAL: Always return valid JSON only. Extract minimum amounts as pure numbers (remove $, commas) or null if not mentioned. Convert natural language ranks to standard format (#1, #2-3, #4-10, etc.). If you find ANY rank/reward pairs, include them. Only return empty array if absolutely no rewards can be identified.`;

      const response = await fetch(
        "https://api.openai.com/v1/chat/completions",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${settings.apiKey}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            model: "gpt-4o",
            messages: [
              {
                role: "system",
                content:
                  "You are an expert at parsing reward information. Always return valid JSON only.",
              },
              {
                role: "user",
                content: prompt,
              },
            ],
            max_tokens: 300,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error?.message || `OpenAI API error: ${response.status}`
        );
      }

      const data = await response.json();
      const content = data.choices?.[0]?.message?.content?.trim();

      if (!content) {
        throw new Error("No rewards parsing generated");
      }

      console.log("🔍 Raw OpenAI response for rank rewards parsing:", content);

      let rewardsData;
      try {
        // Clean the content in case there's extra text
        let cleanContent = content.trim();

        // Extract JSON if it's wrapped in other text
        const jsonMatch = cleanContent.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          cleanContent = jsonMatch[0];
        }

        rewardsData = JSON.parse(cleanContent);
        console.log("✅ Parsed rank rewards data:", rewardsData);
      } catch (parseError) {
        console.error(
          "❌ Error parsing rank rewards response:",
          parseError,
          "Content:",
          content
        );

        // Try to extract rewards manually as fallback
        const fallbackRewards = this.extractRankRewardsFallback(userInput);
        if (fallbackRewards.length > 0) {
          console.log("🔄 Using fallback rank parsing:", fallbackRewards);
          return { success: true, data: fallbackRewards };
        }

        throw new Error("Invalid rewards response format");
      }

      const rewards = rewardsData.rewards || [];
      console.log("🏆 Final rank rewards array:", rewards);

      return { success: true, data: rewards };
    } catch (error) {
      console.error("Error parsing rewards by rank:", error);
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Failed to parse rewards",
      };
    }
  }

  static async generateFundraiserContent(
    description: string,
    conversationHistory: string[] = [],
    additionalAnswers?: string[],
    openaiSettings?: OpenAISettingsDataModel | null
  ): Promise<OpenAIResponse> {
    const settings = openaiSettings;
    if (!settings?.apiKey) {
      return { success: false, error: "OpenAI API key not configured" };
    }
    try {
      let fullContext = `Create fundraiser content for: "${description}"`;

      if (conversationHistory && conversationHistory.length > 0) {
        fullContext += `\n\nFull conversation history:\n${conversationHistory.join("\n")}`;
      }

      if (additionalAnswers && additionalAnswers.length > 0) {
        fullContext += `\n\nAdditional information provided:\n${additionalAnswers.join("\n")}`;
      }

      const prompt = `You are an expert at creating fundraiser content. Carefully analyze the conversation and extract ALL specific information provided by the user. Use EXACT values mentioned by the user, not defaults. If website information is included, prioritize that data.

${fullContext}

CRITICAL INSTRUCTIONS:
1. EXTRACT EXACT VALUES: If user mentions "$25,000" extract as number 25000, "$1,000" as 1000, etc.
2. EXTRACT EXACT DATES: If user says "Start Oct 1" use "2024-10-01", not today's date
3. EXTRACT EXACT NAMES: Use the exact organization/team name provided
4. FOCUS ON USER INPUT: Extract all information from the user's description and conversation
5. CREATE COMPELLING CONTENT: Generate creative mission, impact, stats, and especially a detailed description that tells the story behind the cause. Don't repeat the user's exact words - expand and enhance them into compelling narrative content
6. CHOOSE APPROPRIATE CATEGORY: Select the most relevant category from the list
7. GOAL EXTRACTION: Look for patterns like "$25,000", "25000", "25K", "twenty-five thousand" and convert to number

ANALYZE FOR THESE KEY DETAILS:
- Organization/Team Name (e.g., "HVAA Boys Soccer")
- Fundraising Goal Amount (e.g., "$25,000" = 25000, "25K" = 25000, "$1,000" = 1000)
- Start Date (e.g., "Oct 1" = "2024-10-01")
- End Date (e.g., "Feb 28, 2026" = "2026-02-28")
- Purpose/Use of Funds (e.g., "uniforms and travel")
- Beneficiaries (e.g., "the team")
- Promoter Goal (individual target, usually 1/10 of main goal)
- Marketing/Rewards (incentives for promoters)
- Mission Statement (from user description)
- Contact Information (if provided by user)

DESCRIPTION GENERATION INSTRUCTIONS:
For the description field, create a compelling 2-3 paragraph story that:
1. Introduces the organization/team and their mission
2. Explains why this fundraiser is important and what challenges they face
3. Describes the positive impact donations will have on the beneficiaries
4. Uses emotional, inspiring language that motivates people to donate
5. NEVER includes specific dollar amounts, dates, or repeats the user's exact input
6. Expands on the basic information to create a full narrative

Example: If user says "HVAA Boys Soccer. Benefits the team with funds used for uniforms and travel."
DO NOT write: "HVAA Boys Soccer. Benefits the team with funds used for uniforms and travel."
DO write: "The HVAA Boys Soccer team represents the heart and spirit of our community's youth athletics program. These dedicated young athletes train year-round, developing not just their soccer skills but also teamwork, discipline, and leadership qualities that will serve them throughout their lives. However, like many youth sports programs, the team faces ongoing challenges in providing the essential equipment and opportunities that help these players reach their full potential. Your support will help ensure that every player has access to quality uniforms that build team pride and unity, as well as travel opportunities to compete against other teams, broadening their horizons and testing their skills. By contributing to this fundraiser, you're not just supporting a soccer team – you're investing in the character development and future success of these remarkable young people."

Categories to choose from: Sports & Teams, Education & Schools, Medical & Health, Community Projects, Animals & Pets, Emergency & Crisis Relief, Environmental & Sustainability, Creative & Arts, Youth & Kids, Veterans & Military, Religious & Faith-Based, Nonprofit & Charity, Advocacy & Causes, International & Global Aid, Memorial & Funeral, Celebrations & Events, Entrepreneurship & Startups, Food Instability, Housing Assistance, Human Rights & Equality, Aging & Care, Parenting & Fertility

Return ONLY this JSON structure with extracted and generated content:

{
  "main": {
    "name": "[Extract exact organization name]",
    "category": "[Choose most appropriate category]",
    "title": "[Create compelling 40-char marketing title]",
    "subtitle": "[Create descriptive 50-char subtitle]",
    "description": "[Write a compelling 2-3 paragraph fundraiser description that tells the story of the cause. Extrapolate from the basic information to create an engaging narrative. DO NOT include: dollar amounts, URLs, dates, or repeat the user's exact input. Focus on the mission, the people involved, why this matters, and the positive impact donations will have. Make it emotional and inspiring.]",
    "goal": [Extract exact goal amount as number],
    "startDate": "[Extract exact start date in YYYY-MM-DD format or null]",
    "endDate": "[Extract exact end date in YYYY-MM-DD format or null]",
    "iconImageChoice": "none"
  },
  "mission": {
    "title": "[Create dynamic mission title, not 'Our Mission']",
    "description": "[Write compelling mission statement about the cause, NOT repeating user's description]"
  },
  "impact": [
    {"icon": "[Choose relevant icon]", "color": "#ef4444", "number": "[Relevant number]", "description": "[Impact description]"},
    {"icon": "[Choose relevant icon]", "color": "#3b82f6", "number": "[Relevant number]", "description": "[Impact description]"},
    {"icon": "[Choose relevant icon]", "color": "#10b981", "number": "[Relevant number]", "description": "[Impact description]"},
    {"icon": "[Choose relevant icon]", "color": "#f59e0b", "number": "[Relevant number]", "description": "[Impact description]"}
  ],
  "stats": [
    {"value": "[Relevant stat]", "name": "[Stat name]"},
    {"value": "[Relevant stat]", "name": "[Stat name]"},
    {"value": "[Relevant stat]", "name": "[Stat name]"}
  ],
  "marketing": {
    "goal": 25000,
    "closeOnGoalAchieved": false,
    "increaseGoalOnAchieved": true,
    "increaseGoalPercentage": "10",
    "allowPromoterInvites": true,
    "includeRecruitedActivity": false,
    "rewards": [],
    "ranks": []
  },
  "callToAction": {
    "ctaIconImageChoice": "none",
    "ctaButtonText": "Donate",
    "ctaTitle": "Make a Difference Today",
    "ctaDescription": "Your donation is critically important to achieving our fundraising goals."
  }
}

Generate exactly 4 impact highlights and exactly 3 statistics. Ensure goal amounts are realistic for the cause type. Make all content compelling and professional.`;

      // Use a more reliable model for content generation
      const modelToUse =
        settings.textModel === "gpt-4o" ? "gpt-4o" : settings.textModel;

      const requestBody = {
        model: modelToUse,
        messages: [
          {
            role: "system",
            content:
              "You are an expert fundraiser content creator. Generate compelling, professional fundraiser content based on user descriptions. Always return valid JSON format only. Make content engaging and action-oriented. Ensure goal amounts are realistic for the cause. Choose appropriate icons and colors for the cause type.",
          },
          {
            role: "user",
            content: prompt,
          },
        ],
        max_tokens: 4000,
        temperature: 0.7,
      };

      console.log("🔄 Using model for content generation:", modelToUse);

      console.log("OpenAI Request:", requestBody);

      const response = await fetch(
        "https://api.openai.com/v1/chat/completions",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${settings.apiKey}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );

      console.log("OpenAI Response Status:", response.status);

      if (!response.ok) {
        const errorData = await response.json();
        console.error("OpenAI API Error:", errorData);
        throw new Error(
          errorData.error?.message || `OpenAI API error: ${response.status}`
        );
      }

      const data = await response.json();
      console.log("OpenAI Response Data:", data);

      const content = data.choices?.[0]?.message?.content?.trim();

      if (!content) {
        console.error("No content in response:", {
          choices: data.choices,
          hasChoices: !!data.choices,
          choicesLength: data.choices?.length,
          firstChoice: data.choices?.[0],
          message: data.choices?.[0]?.message,
        });

        console.log("🔄 Attempting content generation retry with gpt-4o...");

        // Retry with gpt-4o and simplified prompt
        try {
          const retryResponse = await fetch(
            "https://api.openai.com/v1/chat/completions",
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${settings.apiKey}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                model: "gpt-4o",
                messages: [
                  {
                    role: "system",
                    content:
                      "Generate fundraiser content in JSON format. Include main (name, category, title, subtitle, description, goal), mission (title, description), impact array, and stats array.",
                  },
                  {
                    role: "user",
                    content: `Create fundraiser content for: "${description}". Return valid JSON only.`,
                  },
                ],
                max_tokens: 2000,
                temperature: 0.5,
              }),
            }
          );

          if (retryResponse.ok) {
            const retryData = await retryResponse.json();
            const retryContent =
              retryData.choices?.[0]?.message?.content?.trim();
            console.log("🔄 Retry content:", retryContent);

            if (retryContent) {
              console.log("✅ Content generation retry successful with gpt-4o");
              return { success: true, data: JSON.parse(retryContent) };
            }
          }
        } catch (retryError) {
          console.error("🔄 Content generation retry failed:", retryError);
        }

        throw new Error("No content generated - API returned empty response");
      }

      console.log("🎯 Generated Content:", content);

      // Parse the JSON response
      let fundraiserData;
      try {
        // Clean the content in case there's extra text
        let cleanContent = content.trim();

        // Extract JSON if it's wrapped in other text
        const jsonMatch = cleanContent.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          cleanContent = jsonMatch[0];
          console.log("🧹 Extracted JSON from wrapped content");
        }

        fundraiserData = JSON.parse(cleanContent);
        console.log("✅ Successfully parsed JSON");
      } catch (parseError) {
        console.error(
          "❌ Error parsing AI response:",
          parseError,
          "Content:",
          content
        );
        console.error("❌ Parse error details:", {
          error: parseError,
          contentLength: content?.length,
          contentStart: content?.substring(0, 100),
          contentEnd: content?.substring(content.length - 100),
        });
        throw new Error(
          `Invalid response format from AI: ${parseError instanceof Error ? parseError.message : "Unknown parse error"}`
        );
      }

      console.log("🎯 Parsed Fundraiser Data:", fundraiserData);

      // Validate required fields with detailed logging
      const missingFields: string[] = [];
      if (!fundraiserData.main) missingFields.push("main");
      if (!fundraiserData.mission) missingFields.push("mission");
      if (!fundraiserData.impact) missingFields.push("impact");
      if (!fundraiserData.stats) missingFields.push("stats");

      if (missingFields.length > 0) {
        console.error("❌ Incomplete data - missing fields:", missingFields);
        console.error("❌ Available fields:", Object.keys(fundraiserData));
        console.error("❌ Full data structure:", fundraiserData);
        throw new Error(
          `Incomplete fundraiser data generated - missing: ${missingFields.join(", ")}`
        );
      }

      console.log("✅ All required fields present");

      return { success: true, data: fundraiserData };
    } catch (error) {
      console.error("❌ Error generating fundraiser content:", error);
      console.error("❌ Error details:", {
        errorType:
          error instanceof Error ? error.constructor.name : typeof error,
        errorMessage: error instanceof Error ? error.message : String(error),
        errorStack: error instanceof Error ? error.stack : undefined,
      });

      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to generate fundraiser content",
      };
    }
  }

  // Fallback method to extract rank rewards manually using regex
  extractRankRewardsFallback(userInput: string): any[] {
    const rewards: any[] = [];

    // Patterns to match rank-based reward structures
    const patterns = [
      // "$500 gift card if #1 position and raise a minimum of 10,000"
      /\$?([0-9,]+)\s+([^if]+?)\s+if\s+#?([0-9-]+)\s+position.*?(?:minimum|min).*?\$?([0-9,]+)/gi,
      // "#1 gets trip, must raise $2500"
      /#?([0-9-]+)\s+gets?\s+([^,]+).*?(?:minimum|min|raise).*?\$?([0-9,]+)/gi,
      // "Trip for #1 rank, minimum $2500"
      /([^,]+?)\s+for\s+#?([0-9-]+).*?(?:minimum|min).*?\$?([0-9,]+)/gi,
      // "Winner gets $100 gift card"
      /(winner|top|first)\s+gets?\s+\$?([0-9,]+)\s*([^,]*)/gi,
    ];

    let order = 1;

    for (const pattern of patterns) {
      let match;
      while ((match = pattern.exec(userInput)) !== null) {
        let rank, reward, minimum;

        if (pattern.source.includes("if\\s+#?([0-9-]+)\\s+position")) {
          // Pattern: "$500 gift card if #1 position and raise a minimum of 10,000"
          const amount = match[1]?.replace(/,/g, "");
          const rewardType = match[2]?.trim();
          rank = match[3];
          minimum = parseInt(match[4]?.replace(/,/g, ""));
          reward = `$${amount} ${rewardType}`;
        } else if (pattern.source.includes("#?([0-9-]+)\\s+gets?")) {
          // Pattern: "#1 gets trip, must raise $2500"
          rank = match[1];
          reward = match[2]?.trim();
          minimum = parseInt(match[3]?.replace(/,/g, ""));
        } else if (pattern.source.includes("for\\s+#?([0-9-]+)")) {
          // Pattern: "Trip for #1 rank, minimum $2500"
          reward = match[1]?.trim();
          rank = match[2];
          minimum = parseInt(match[3]?.replace(/,/g, ""));
        } else if (pattern.source.includes("(winner|top|first)")) {
          // Pattern: "Winner gets $100 gift card"
          const position = match[1]?.toLowerCase();
          const amount = match[2]?.replace(/,/g, "");
          const rewardType = match[3]?.trim();
          rank =
            position === "winner" || position === "top" || position === "first"
              ? "1"
              : "1";
          reward = `$${amount} ${rewardType}`.trim();
          minimum = null;
        }

        if (rank && reward) {
          // Normalize rank format
          const normalizedRank = rank.includes("-") ? `#${rank}` : `#${rank}`;

          // Check if we already have this rank/reward combo
          const exists = rewards.some(
            (r) =>
              r.rank === normalizedRank &&
              r.reward.toLowerCase() === reward.toLowerCase()
          );

          if (!exists) {
            rewards.push({
              rank: normalizedRank,
              reward: reward.charAt(0).toUpperCase() + reward.slice(1),
              minimum: minimum || null,
              order: order++,
            });
          }
        }
      }
    }

    // Sort by rank (try to put single ranks before ranges)
    rewards.sort((a, b) => {
      const aNum = parseInt(a.rank.replace("#", "").split("-")[0]);
      const bNum = parseInt(b.rank.replace("#", "").split("-")[0]);
      return aNum - bNum;
    });

    // Update order after sorting
    rewards.forEach((reward, index) => {
      reward.order = index + 1;
    });

    return rewards;
  }

  // Fallback method to extract rewards manually using regex
  extractRewardsFallback(userInput: string): any[] {
    const rewards: any[] = [];

    // Patterns to match reward and amount pairs
    const patterns = [
      /(\w+(?:\s+\w+)*)\s*(?:@|at|for)\s*\$?([0-9,]+)(?:\s+raised)?/gi,
      /(\w+(?:\s+\w+)*)\s*(?:if|when).*?\$?([0-9,]+)/gi,
      /\$?([0-9,]+).*?(\w+(?:\s+\w+)*)/gi,
    ];

    let order = 1;

    for (const pattern of patterns) {
      let match;
      while ((match = pattern.exec(userInput)) !== null) {
        const reward = match[1]?.trim();
        const amountStr = match[2]?.replace(/,/g, "");
        const amount = parseInt(amountStr);

        if (reward && !isNaN(amount) && amount > 0) {
          // Check if we already have this reward/amount combo
          const exists = rewards.some(
            (r) =>
              r.reward.toLowerCase() === reward.toLowerCase() &&
              r.level === amount
          );

          if (!exists) {
            rewards.push({
              level: amount,
              reward:
                reward.charAt(0).toUpperCase() + reward.slice(1).toLowerCase(),
              order: order++,
            });
          }
        }
      }
    }

    // Sort by amount (lowest first)
    rewards.sort((a, b) => a.level - b.level);

    // Update order after sorting
    rewards.forEach((reward, index) => {
      reward.order = index + 1;
    });

    return rewards;
  }

}


// // Global function for manually updating API key (accessible from browser console)
// (window as any).updateOpenAIKey = () => {
//   OpenAIService.updateApiKey();
//   console.log('✅ OpenAI API key has been updated. Refresh the page to ensure all components use the new key.');
// };
